import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const GroupCard = ({ group, onViewDetails, onAssignPilgrims, onManageItinerary, onCommunicate, currentLanguage }) => {
  const labels = {
    fr: {
      capacity: 'Capacité',
      remaining: 'Places restantes',
      supervisor: 'Superviseur',
      departure: 'Départ',
      revenue: 'Revenu',
      viewDetails: 'Voir détails',
      assignPilgrims: 'Assigner pèlerins',
      itinerary: 'Itinéraire',
      communicate: 'Communiquer',
      synced: 'Synchronisé avec Nusuk',
      pending: 'En attente de synchronisation'
    },
    ar: {
      capacity: 'السعة',
      remaining: 'الأماكن المتبقية',
      supervisor: 'المشرف',
      departure: 'المغادرة',
      revenue: 'الإيرادات',
      viewDetails: 'عرض التفاصيل',
      assignPilgrims: 'تعيين الحجاج',
      itinerary: 'المسار',
      communicate: 'التواصل',
      synced: 'متزامن مع نسك',
      pending: 'في انتظار المزامنة'
    }
  };

  const l = labels?.[currentLanguage];
  const capacityPercentage = (group?.assignedPilgrims / group?.capacity) * 100;
  const remainingSpots = group?.capacity - group?.assignedPilgrims;

  const getStatusColor = () => {
    if (capacityPercentage >= 90) return 'bg-success text-success-foreground';
    if (capacityPercentage >= 70) return 'bg-warning text-warning-foreground';
    return 'bg-primary text-primary-foreground';
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-warm hover:shadow-warm-md transition-smooth overflow-hidden">
      <div className="p-4 md:p-6">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-lg md:text-xl font-semibold text-foreground truncate">
                {group?.name}
              </h3>
              <span className={`px-2 py-1 rounded-md text-xs font-medium ${
                group?.type === 'Omra' ? 'bg-accent/10 text-accent' : 'bg-secondary/10 text-secondary'
              }`}>
                {group?.type}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Icon name="Calendar" size={16} />
              <span>{l?.departure}: {group?.departureDate}</span>
            </div>
          </div>
          <div className={`px-3 py-1 rounded-md text-xs font-medium ${getStatusColor()}`}>
            {remainingSpots} {l?.remaining}
          </div>
        </div>

        <div className="space-y-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
              <Image
                src={group?.supervisorAvatar}
                alt={group?.supervisorAvatarAlt}
                className="w-full h-full rounded-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-foreground truncate">{group?.supervisorName}</p>
              <p className="caption text-muted-foreground">{l?.supervisor}</p>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">{l?.capacity}</span>
              <span className="font-medium text-foreground">
                {group?.assignedPilgrims}/{group?.capacity}
              </span>
            </div>
            <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
              <div
                className={`h-full transition-all duration-300 ${
                  capacityPercentage >= 90 ? 'bg-success' : capacityPercentage >= 70 ? 'bg-warning' : 'bg-primary'
                }`}
                style={{ width: `${capacityPercentage}%` }}
              />
            </div>
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-border">
            <span className="text-sm text-muted-foreground">{l?.revenue}</span>
            <span className="text-lg font-semibold text-foreground data-text">
              {group?.revenue?.toLocaleString('fr-MA')} MAD
            </span>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-4">
          <Icon
            name={group?.nusukSynced ? 'CheckCircle2' : 'Clock'}
            size={16}
            className={group?.nusukSynced ? 'text-success' : 'text-warning'}
          />
          <span className="caption text-muted-foreground">
            {group?.nusukSynced ? l?.synced : l?.pending}
          </span>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button
            variant="default"
            size="sm"
            iconName="Eye"
            iconPosition="left"
            onClick={() => onViewDetails(group)}
            fullWidth
          >
            {l?.viewDetails}
          </Button>
          <Button
            variant="outline"
            size="sm"
            iconName="UserPlus"
            iconPosition="left"
            onClick={() => onAssignPilgrims(group)}
            fullWidth
          >
            {l?.assignPilgrims}
          </Button>
          <Button
            variant="secondary"
            size="sm"
            iconName="Map"
            iconPosition="left"
            onClick={() => onManageItinerary(group)}
            fullWidth
          >
            {l?.itinerary}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            iconName="MessageSquare"
            iconPosition="left"
            onClick={() => onCommunicate(group)}
            fullWidth
          >
            {l?.communicate}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GroupCard;